import type { CalendarConnection, ExternalCalendarContext, ExternalCalendarEvent } from "../../domain/app-types.ts";

export interface ExternalCalendarContextState {
  events: ExternalCalendarEvent[];
  errors: ExternalCalendarContext["errors"];
  loading: boolean;
}

export interface ExternalCalendarContextCapabilities {
  calendarActive(): boolean;
  enabled(): boolean;
  online(): boolean;
  connections(): CalendarConnection[];
  range(): { start: string; end: string };
  request(path: string, init?: RequestInit): Promise<Response>;
  setState(state: ExternalCalendarContextState): void;
  render(): Promise<void>;
}

export async function loadCalendarConnections(request: ExternalCalendarContextCapabilities["request"]): Promise<CalendarConnection[] | null> {
  const response = await request("/api/v1/calendar-connections", { credentials: "include" }).catch(() => null);
  return response?.ok ? response.json() : null;
}

export async function refreshExternalCalendarContext(capabilities: ExternalCalendarContextCapabilities): Promise<void> {
  if (!capabilities.calendarActive()) { await capabilities.render(); return; }
  const connections = capabilities.connections();
  if (!capabilities.enabled() || !connections.length || !capabilities.online()) {
    capabilities.setState({ events: [], errors: [], loading: false });
    await capabilities.render();
    return;
  }
  capabilities.setState({ events: [], errors: [], loading: true });
  const { start, end } = capabilities.range();
  const response = await capabilities.request(`/api/v1/calendar-context?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`, { credentials: "include" }).catch(() => null);
  if (response?.ok) {
    const context = await response.json() as ExternalCalendarContext;
    capabilities.setState({ events: context.events || [], errors: context.errors || [], loading: false });
  } else {
    capabilities.setState({ events: [], errors: connections.map(connection => ({ provider: connection.provider, message: "Calendar context could not be loaded" })), loading: false });
  }
  await capabilities.render();
}

export async function reconcileExternalCalendars(connections: CalendarConnection[], online: boolean, request: ExternalCalendarContextCapabilities["request"]): Promise<number | null> {
  if (!connections.length || !online) return null;
  const response = await request("/api/v1/calendar-sync", { method: "POST", credentials: "include" }).catch(() => null);
  if (!response?.ok) return null;
  return Number((await response.json()).synchronized || 0);
}
